import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { NewOrExistingFile } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import { CreateUpload } from "@coworker/apprestructured/src/task-management/types/Upload";
import { uploadToImage } from "../Helpers/imageHelpers";
import { QueryKeys } from "./queryKeys";
import { useAddImageToMedia } from "./useAddImageToMedia";

interface UseAddMediaImagesParams {
  images: NewOrExistingFile[];
  imagesCreateUploads: CreateUpload[];
}

export const useAddMediaImages = ({
  images,
  imagesCreateUploads,
}: UseAddMediaImagesParams) => {
  const queryClient = useQueryClient();
  const addImageToMedia = useAddImageToMedia();
  const [error, setError] = useState<boolean>(false);

  const addMediaImages = useCallback(
    async (mediaId: string, groupId?: string) => {
      setError(false);
      const uploads = imagesCreateUploads.map((uploads, index) =>
        uploadToImage(uploads, mediaId, index, images[index]?.rotation ?? 0)
      );

      try {
        await Promise.all(
          uploads.map((upload) => {
            const mutation = { ...upload };
            if (groupId) mutation.groupId = groupId;
            return addImageToMedia.mutateAsync(mutation);
          })
        );
      } catch {
        setError(true);
      }

      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.ImagesByMedia],
      });
    },
    [addImageToMedia, images, imagesCreateUploads, queryClient, setError]
  );

  return { addMediaImages, error };
};
